/**
 * ChatNode.tsx — Mini-chat focado em uma nota específica
 *
 * Contextualiza a conversa com o conteúdo da nota aberta no painel.
 * Envia o note_id para a Cloud Function, que prioriza chunks desta nota.
 */

import { useState, useRef, useEffect, type FormEvent } from 'react'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  loading?: boolean
}

interface ChatNodeProps {
  noteId: string
  noteTitle: string
}

export function ChatNode({ noteId, noteTitle }: ChatNodeProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Limpa conversa quando troca de nota
  useEffect(() => {
    setMessages([])
  }, [noteId])

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault()
    const question = input.trim()
    if (!question || loading) return

    const userMsg: Message = { id: `user-${Date.now()}`, role: 'user', content: question }
    const loadingMsg: Message = { id: `loading-${Date.now()}`, role: 'assistant', content: '', loading: true }

    setMessages((prev) => [...prev, userMsg, loadingMsg])
    setInput('')
    setLoading(true)

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          focus_note_id: noteId, // Cloud Function prioriza este nó
          history: messages.slice(-4).map((m) => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }],
          })),
        }),
      })

      const data = await response.json()

      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { id: `assistant-${Date.now()}`, role: 'assistant', content: data.answer, loading: false }
            : m
        )
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido'
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { id: `error-${Date.now()}`, role: 'assistant', content: `Erro: ${msg}`, loading: false }
            : m
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        height: '300px',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          fontSize: '11px',
          color: '#5a5a80',
        }}
      >
        Chat focado em: <span style={{ color: '#57f5c3' }}>{noteTitle}</span>
      </div>

      <div
        style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}
      >
        {messages.length === 0 && (
          <div style={{ color: '#5a5a80', fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>
            Pergunte sobre esta nota
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '90%',
              padding: '6px 10px',
              borderRadius: '8px',
              fontSize: '12px',
              background:
                msg.role === 'user'
                  ? 'rgba(87, 245, 195, 0.12)'
                  : 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: msg.loading ? '#5a5a80' : '#e8e8f0',
            }}
          >
            {msg.loading ? '...' : msg.content}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={sendMessage}
        style={{ padding: '10px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '6px' }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Pergunta sobre esta nota..."
          disabled={loading}
          style={{ flex: 1, fontSize: '12px' }}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="btn btn-primary"
          style={{ padding: '5px 10px', fontSize: '12px' }}
        >
          →
        </button>
      </form>
    </div>
  )
}
